import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { classNames } from "../../helpers/classNames";
import Button from "../../components/form/Button";
import { lngDetect } from "../../helpers/lngDetect";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { formatEventDate } from "../../util/Flag";
import { t } from "i18next";

const menuItems = (promoter, lang) => {
  return [
    {
      label: "Dashboard",
      to: `/${lang}/promotion-events-dashboard/${promoter?.uid}`,
      subItems: [],
    },
    {
      label: "Sales",
      to: `/${lang}/promotion-events-sales/${promoter?.uid}`,
      subItems: [],
    },
    {
      label: "Settlement",
      to: `/${lang}/promotion-events-settlement/${promoter?.uid}`,
      subItems: [],
    },
    {
      label: "View commissions",
      to: `/${lang}/promotion-events-commission/${promoter?.uid}`,
      subItems: [],
    },
    // {
    //   label: "Web Integration",
    //   subItems: [
    //     { label: "Sales Report", to: `/${lang}/user/myEvent/${promoter?.uid}` },
    //     {
    //       label: "Customer Report",
    //       to: `/${lang}/user/customer_reports/${promoter?.uid}`,
    //     },
    //     {
    //       label: "Revenue Analytics",
    //       to: `/${lang}/user/myEvent/${promoter?.uid}`,
    //     },
    //   ],
    // },
    // {
    //   label: "Preferences",
    //   subItems: [
    //     { label: "Lists", to: `/${lang}/user/promoters/${promoter?.uid}` },
    //   ],
    // },
  ];
};

const PromoterInnerPanel = ({ promoterDetails = {}, selectedLanguage }) => {
  const [openMenus, setOpenMenus] = useState({});
  const { lang } = useSelector((state) => state?.langProvider);
  const location = useLocation();

  const steps = [
    { label: t("Build event page"), link: `/${lang}/user/events/event-manage` },
  ];

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Helper function to check if any submenu item is active
  const isParentActive = (subItems) => {
    return subItems.some((sub) => location.pathname === sub.to);
  };

  const currentStep = 2;

  return (
    <div className="w-full bg-white rounded-md overflow-auto h-full py-5 px-5 scroll-smooth scrollbar scrollbar-default">
      {/* Back Link */}
      <div className="relative mb-3 flex">
        <Button
          buttonLabel={t("Back to promotion events")}
          buttonLabelClasses={"text-deep-purple capitalize"}
          buttonIcon={"fa-regular fa-arrow-left"}
          buttonClasses={"bg-transparent px-0 h-auto text-deep-purple"}
          buttonHasLink
          buttonLink={`/${selectedLanguage}/promotion-events`}
        />
      </div>

      {/* Event Card */}
      <div className="group relative shadow rounded-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 border border-transparent hover:border-primary/10 hover:no-underline">
        <div className="p-4">
          <h2 className="capitalize font-semibold text-base text-deep-purple mb-2 font-Montserrat">
            {lngDetect(promoterDetails?.eventData?.title)}
          </h2>
          <p className="text-xs font-medium text-deep-purple mb-3 flex gap-1 items-start">
            <i className="fa-regular fa-calendar-days mt-1"></i>
            {formatEventDate(
              promoterDetails?.eventData?.startDate,
              promoterDetails?.eventData?.endDate
            )}
          </p>
          <div className="flex items-center justify-between">
            <Link
              target="_blank"
              to={`/${lang}/event-details/${promoterDetails?.eventData?.uid}`}
              className="text-deep-purple text-xs hover:underline ml-auto"
            >
              <div className="flex font-medium gap-2 items-center">
                {t("View")}
                <i className="fa-solid fa-up-right-from-square"></i>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="my-6">
        <h3 className="text-xs font-semibold text-deep-purple uppercase mb-2 tracking-wide">
          {t("Steps")}
        </h3>
        <ul className="space-y-2">
          {steps.map((step, idx) => {
            const isActive = idx === currentStep;

            return (
              <li key={idx}>
                <Link
                  to={step?.link}
                  className={classNames(
                    "flex items-center gap-3 text-sm px-3 py-2 rounded-md shadow-sm transition font-medium",
                    "hover:bg-primary/10",
                    isActive ? "bg-primary text-white" : " text-deep-purple"
                  )}
                >
                  <span
                    className={classNames(
                      "flex items-center justify-center w-5 h-5 rounded-full border-2 text-xs shrink-0",
                      isActive
                        ? "bg-deep-purple border-deep-purple text-white"
                        : "border-deep-purple text-deep-purple"
                    )}
                  >
                    ✓
                  </span>
                  <span>{t(step?.label)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Navigation Menu */}
      <nav>
        <ul className="space-y-0">
          {menuItems(promoterDetails, selectedLanguage)?.map(
            ({ label, subItems, to }, index) => {
              const hasSubItems = subItems && subItems.length > 0;
              const hasActiveSubItem = hasSubItems && isParentActive(subItems);

              return (
                <li key={label}>
                  {/* Main Menu Item */}
                  {hasSubItems ? (
                    <button
                      onClick={() => toggleMenu(label)}
                      className={classNames(
                        "w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors duration-200 font-medium text-sm",
                        hasActiveSubItem
                          ? "text-pink-500 font-semibold bg-pink-50"
                          : "text-gray-700",
                        index <
                          menuItems(promoterDetails, selectedLanguage).length -
                            1
                          ? "border-b border-gray-200"
                          : ""
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span>{t(label)}</span>
                        <i
                          className={`fa-solid text-xs transition-transform duration-200 ${
                            openMenus[label]
                              ? "fa-caret-down"
                              : "fa-caret-right"
                          }`}
                        ></i>
                      </div>
                    </button>
                  ) : (
                    <NavLink
                      to={to || "#"}
                      className={({ isActive }) =>
                        classNames(
                          "block w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors duration-200 font-medium text-sm",
                          isActive
                            ? "text-pink-500 font-semibold bg-pink-50"
                            : "text-gray-700",
                          index <
                            menuItems(promoterDetails, selectedLanguage)
                              .length -
                              1
                            ? "border-b border-gray-200"
                            : ""
                        )
                      }
                    >
                      {t(label)}
                    </NavLink>
                  )}

                  {/* Dropdown Links */}
                  {hasSubItems && (
                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        openMenus[label]
                          ? "max-h-96 opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <ul className="bg-gray-50">
                        {subItems.map((sub, i) => (
                          <li key={i}>
                            <NavLink
                              to={sub?.to}
                              className={({ isActive }) =>
                                classNames(
                                  "block py-3 px-8 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors duration-200 text-sm",
                                  isActive
                                    ? "bg-pink-100 text-pink-600 border-r-2 border-pink-500 font-semibold"
                                    : ""
                                )
                              }
                            >
                              {sub.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            }
          )}
        </ul>
      </nav>
    </div>
  );
};

export default PromoterInnerPanel;
